import { useEffect, useState } from 'react';
import { useLoginGuest } from '@carlito/hub-react';
import type { ListBookingsController } from '@carlito/hotel-apis';
import { ControllerExecutionSuccessResult } from '@carlito/ddd';

type Participant = {
  isMainBooker: boolean;
  id: string;
  displayName: string;
};

type ListBookingsResponse = ControllerExecutionSuccessResult<ListBookingsController>;
type InitialBookingData = ListBookingsResponse['bookings'][0];

type Params = {
  initialBookingData?: Partial<InitialBookingData>;
};

export function usePmsEventBooking({ initialBookingData }: Params = {}) {
  const guestLoginMutation = useLoginGuest();

  const [allParticipants, setAllParticipant] = useState<Participant[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);

  useEffect(() => {
    const initialParticipants = initialBookingData?.participants
      ? initialBookingData.participants?.map((el) => ({
          displayName: el.name ?? '',
          id: el.carlitoHubUserId ?? '',
          isMainBooker: el.isMainBooker ?? false,
        }))
      : [];
    setAllParticipant(initialParticipants);
    setSelectedParticipants(initialParticipants.map((el) => el.id));
  }, [initialBookingData?.participants?.length]);

  const [isLoadingGuest, setIsLoadingGuest] = useState<boolean>(false);
  const [loadGuestError, setLoadGuestError] = useState<string>('');

  const loginGuest = async ({
    hotelId,
    birthDate,
    bookingReference,
    roomNumber,
  }: {
    birthDate?: string;
    hotelId: string;
    roomNumber?: string;
    bookingReference?: string;
  }) => {
    let isLoggedIn = false;
    let loggedInUserId = undefined;
    let hasMultipleParticipants = false;

    try {
      setIsLoadingGuest(true);
      setLoadGuestError('');
      const loginResult = await guestLoginMutation.mutateAsync({
        hotelId,
        birthDate,
        bookingReference: bookingReference ? bookingReference : undefined,
        roomNumber,
      });
      setIsLoadingGuest(false);
      const loggedInGuest = loginResult.guest;

      if (!loggedInGuest) {
        setLoadGuestError('Guest not found. Please check the details and try again.');
      } else {
        isLoggedIn = true;
        loggedInUserId = loggedInGuest.id;
        if (
          loginResult &&
          loginResult?.currentReservation &&
          loginResult?.currentReservation.guests &&
          loginResult?.currentReservation?.guests?.length > 1
        ) {
          hasMultipleParticipants = true;
          const participants = loginResult?.currentReservation?.guests
            ?.sort((a, b) => {
              if (a.data.id === loginResult?.guest.id) {
                return -1;
              }
              if (b.data.id === loginResult?.guest.id) {
                return 1;
              }
              return 0;
            })
            .map((participant) => ({
              id: participant.data.id,
              isMainBooker: participant.data.id === loginResult?.guest.id,
              displayName: `${participant.data.firstName} ${participant.data.lastName}`,
            }));
          setAllParticipant(participants);
          setSelectedParticipants([participants[0].id]); //select the current user by default
        }
      }
      return { isLoggedIn, loggedInUserId, hasMultipleParticipants };
    } catch (e) {
      setIsLoadingGuest(false);
      setLoadGuestError('Guest not found. Please check the details and try again.');
      return { isLoggedIn, loggedInUserId, hasMultipleParticipants };
    }
  };

  const resetValues: () => void = () => {
    setAllParticipant([]);
    setSelectedParticipants([]);
    setLoadGuestError('');
  };
  return {
    resetValues,
    loginGuest,
    allParticipants,
    selectedParticipants,
    setSelectedParticipants,
    isLoadingGuest,
    loadGuestError,
  };
}

export default usePmsEventBooking;
